/* PWA update manager for HalloGBS.
   - Registers /sw.js, detects a waiting (updated) service worker.
   - Exposes subscribe() so a banner can show "Versi baru tersedia".
   - applyUpdate(): activates the waiting worker (SKIP_WAITING) then reloads ONCE on controllerchange.
   - forceRefreshApp(): manual fallback — checks for update, else clears only hallogbs-* caches and reloads once.
   - Never touches localStorage / tokens / IndexedDB. */

let refreshing = false; // guards against reload loops
let waitingWorker = null;
let registration = null;
const listeners = new Set();

function notify() {
  listeners.forEach((cb) => {
    try { cb(!!waitingWorker); } catch (_) { /* noop */ }
  });
}

export function subscribeUpdate(cb) {
  listeners.add(cb);
  if (waitingWorker) cb(true);
  return () => listeners.delete(cb);
}

export function registerSW() {
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener("load", async () => {
    try {
      registration = await navigator.serviceWorker.register("/sw.js");

      // An update was already downloaded and is waiting.
      if (registration.waiting && navigator.serviceWorker.controller) {
        waitingWorker = registration.waiting;
        notify();
      }

      registration.addEventListener("updatefound", () => {
        const nw = registration.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          // A new worker finished installing while an old one controls the page => update ready.
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            waitingWorker = nw;
            notify();
          }
        });
      });

      // Lightweight periodic check for a fresh deploy (hourly).
      setInterval(() => registration && registration.update().catch(() => {}), 60 * 60 * 1000);
    } catch (_) {
      /* registration failure must never blank the app */
    }
  });
}

export function applyUpdate() {
  const w = waitingWorker || (registration && registration.waiting);
  if (w) {
    w.postMessage({ type: "SKIP_WAITING" });
    return; // controllerchange handler reloads once
  }
  // No waiting worker: try a fresh check, else just reload once.
  if (navigator.serviceWorker) {
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg && reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
      else if (!refreshing) { refreshing = true; window.location.reload(); }
    }).catch(() => { if (!refreshing) { refreshing = true; window.location.reload(); } });
  } else if (!refreshing) {
    refreshing = true;
    window.location.reload();
  }
}

export async function forceRefreshApp() {
  try {
    if ("serviceWorker" in navigator) {
      const reg = registration || (await navigator.serviceWorker.getRegistration());
      if (reg) {
        await reg.update().catch(() => {});
        if (reg.waiting) {
          reg.waiting.postMessage({ type: "SKIP_WAITING" }); // reload via controllerchange
          return;
        }
      }
    }
    // No waiting worker: clear ONLY our app caches (keeps localStorage/token/IndexedDB), then reload once.
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.filter((k) => k.startsWith("hallogbs-")).map((k) => caches.delete(k)));
    }
  } catch (_) {
    /* ignore, still reload */
  }
  if (!refreshing) { refreshing = true; window.location.reload(); }
}
